import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Image,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import {
  ITEM_IMAGE_TOO_LARGE_MESSAGE,
  pickAndUploadItemImage,
} from "@/lib/media/businessImages";
import {
  getFriendlyImageUploadError,
  isImageUploadCancelled,
  type ImageUploadProgress,
} from "@/lib/imageUpload";
import {
  CUSTOM_ITEM_PRICE_UNIT,
  ITEM_DESCRIPTION_MAX_WORDS,
  ITEM_PRICE_TYPE_OPTIONS,
  ITEM_PRICE_UNIT_OPTIONS,
  countWords,
  formatItemPrice,
  isItemPriceType,
  isPresetItemPriceUnit,
  itemPriceTypeUsesAmount,
  normalizeCustomPriceUnit,
  normalizeItemDescription,
  readPositiveAmount,
  validateCustomPriceUnit,
  type ItemPriceType,
  type ItemPriceUnit,
  type PresetItemPriceUnit,
} from "@/lib/itemPricing";
import type { BusinessItem } from "../types";

type Props = {
  businessId: string | null;
  onAdd: (item: BusinessItem) => void;
  editingItem?: BusinessItem | null;
  onUpdate?: (item: BusinessItem) => void;
  onCancelEdit?: () => void;
};

function amountToInput(value: unknown) {
  const amount = readPositiveAmount(value);
  return amount === null ? "" : String(amount);
}

function sanitizeAmountInput(value: string) {
  return value.replace(/[^\d.,-]/g, "");
}

function withoutOptionalItemDetails(item: BusinessItem) {
  const next = { ...item };

  delete next.description;
  delete next.priceType;
  delete next.priceAmount;
  delete next.priceMin;
  delete next.priceMax;
  delete next.priceUnit;
  delete next.customPriceUnit;

  return next;
}

export function AddItemForm({
  businessId,
  onAdd,
  editingItem = null,
  onUpdate,
  onCancelEdit,
}: Props) {
  const isEditing = useMemo(() => !!editingItem, [editingItem]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [imageWidth, setImageWidth] = useState<number | null>(null);
  const [imageHeight, setImageHeight] = useState<number | null>(null);
  const [priceType, setPriceType] = useState<ItemPriceType | null>(null);
  const [priceAmountText, setPriceAmountText] = useState("");
  const [priceMinText, setPriceMinText] = useState("");
  const [priceMaxText, setPriceMaxText] = useState("");
  const [priceUnit, setPriceUnit] = useState<ItemPriceUnit | null>(null);
  const [customPriceUnit, setCustomPriceUnit] = useState("");
  const [busyImage, setBusyImage] = useState(false);
  const [busySubmit, setBusySubmit] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [priceAmountError, setPriceAmountError] = useState<string | null>(null);
  const [priceMinError, setPriceMinError] = useState<string | null>(null);
  const [priceMaxError, setPriceMaxError] = useState<string | null>(null);
  const [customPriceUnitError, setCustomPriceUnitError] = useState<
    string | null
  >(null);
  const nameInputRef = useRef<TextInput | null>(null);

  function resetForm() {
    setName("");
    setDescription("");
    setImageUri(null);
    setImageWidth(null);
    setImageHeight(null);
    setPriceType(null);
    setPriceAmountText("");
    setPriceMinText("");
    setPriceMaxText("");
    setPriceUnit(null);
    setCustomPriceUnit("");
    setBusySubmit(false);
    setUploadError(null);
    setUploadStatus(null);
    setUploadPercent(null);
    setNameError(null);
    setImageError(null);
    setDescriptionError(null);
    setPriceAmountError(null);
    setPriceMinError(null);
    setPriceMaxError(null);
    setCustomPriceUnitError(null);
  }

  useEffect(() => {
    if (editingItem) {
      const nextPriceType = isItemPriceType(editingItem.priceType)
        ? editingItem.priceType
        : null;
      const hasPricedType =
        nextPriceType &&
        itemPriceTypeUsesAmount(nextPriceType);
      const normalizedCustomPriceUnit = normalizeCustomPriceUnit(
        editingItem.customPriceUnit
      );
      const nextPriceUnit: ItemPriceUnit | null =
        hasPricedType &&
        editingItem.priceUnit === CUSTOM_ITEM_PRICE_UNIT &&
        normalizedCustomPriceUnit
          ? CUSTOM_ITEM_PRICE_UNIT
          : hasPricedType && isPresetItemPriceUnit(editingItem.priceUnit)
          ? editingItem.priceUnit
          : null;
      const nextCustomPriceUnit =
        nextPriceUnit === CUSTOM_ITEM_PRICE_UNIT
          ? normalizedCustomPriceUnit
          : "";

      setName(editingItem.name ?? "");
      setDescription(editingItem.description ?? "");
      setImageUri(editingItem.imageUri ?? editingItem.imageUrl ?? null);
      setImageWidth(editingItem.imageWidth ?? null);
      setImageHeight(editingItem.imageHeight ?? null);
      setPriceType(nextPriceType);
      setPriceAmountText(
        nextPriceType === "fixed" || nextPriceType === "starting_from"
          ? amountToInput(editingItem.priceAmount)
          : ""
      );
      setPriceMinText(
        nextPriceType === "range" ? amountToInput(editingItem.priceMin) : ""
      );
      setPriceMaxText(
        nextPriceType === "range" ? amountToInput(editingItem.priceMax) : ""
      );
      setPriceUnit(nextPriceUnit);
      setCustomPriceUnit(nextCustomPriceUnit);
      setBusySubmit(false);
      setUploadError(null);
      setUploadStatus(null);
      setUploadPercent(null);
      setNameError(null);
      setImageError(null);
      setDescriptionError(null);
      setPriceAmountError(null);
      setPriceMinError(null);
      setPriceMaxError(null);
      setCustomPriceUnitError(null);
      return;
    }

    resetForm();
  }, [editingItem]);

  function setAmountText(
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
    clearError: () => void
  ) {
    setter(sanitizeAmountInput(value));
    clearError();
  }

  function clearPricingErrors() {
    setPriceAmountError(null);
    setPriceMinError(null);
    setPriceMaxError(null);
    setCustomPriceUnitError(null);
  }

  function handlePriceTypeChange(nextType: ItemPriceType) {
    setPriceType(nextType);
    clearPricingErrors();

    if (nextType === "range") {
      setPriceAmountText("");
      return;
    }

    setPriceMinText("");
    setPriceMaxText("");

    if (nextType === "contact") {
      setPriceAmountText("");
      setPriceUnit(null);
      setCustomPriceUnit("");
    }
  }

  function clearPriceType() {
    setPriceType(null);
    setPriceAmountText("");
    setPriceMinText("");
    setPriceMaxText("");
    setPriceUnit(null);
    setCustomPriceUnit("");
    clearPricingErrors();
  }

  function togglePriceUnit(nextUnit: PresetItemPriceUnit) {
    setPriceUnit((current) => (current === nextUnit ? null : nextUnit));
    setCustomPriceUnit("");
    setCustomPriceUnitError(null);
  }

  function selectCustomPriceUnit() {
    setPriceUnit(CUSTOM_ITEM_PRICE_UNIT);
    setCustomPriceUnitError(null);
  }

  function clearCustomPriceUnit() {
    setPriceUnit(null);
    setCustomPriceUnit("");
    setCustomPriceUnitError(null);
  }

  function handleCustomPriceUnitChange(value: string) {
    setCustomPriceUnit(value);

    if (!customPriceUnitError) return;

    const nextValidation = validateCustomPriceUnit(value);

    if (!nextValidation.error) {
      setCustomPriceUnitError(null);
    }
  }

  function buildOptionalItemDetails() {
    const details: Partial<BusinessItem> = {};
    const normalizedDescription = normalizeItemDescription(description);

    if (normalizedDescription) {
      details.description = normalizedDescription;
    }

    if (!priceType) {
      return details;
    }

    details.priceType = priceType;

    if (priceType === "contact") {
      return details;
    }

    if (priceUnit === CUSTOM_ITEM_PRICE_UNIT) {
      const normalizedCustomUnit = normalizeCustomPriceUnit(customPriceUnit);

      if (normalizedCustomUnit) {
        details.priceUnit = CUSTOM_ITEM_PRICE_UNIT;
        details.customPriceUnit = normalizedCustomUnit;
      }
    } else if (priceUnit) {
      details.priceUnit = priceUnit;
    }

    if (priceType === "range") {
      details.priceMin = readPositiveAmount(priceMinText);
      details.priceMax = readPositiveAmount(priceMaxText);
      return details;
    }

    details.priceAmount = readPositiveAmount(priceAmountText);
    return details;
  }

  function renderAmountInput({
    label,
    value,
    onChangeText,
    error,
    placeholder,
  }: {
    label: string;
    value: string;
    onChangeText: (value: string) => void;
    error: string | null;
    placeholder: string;
  }) {
    return (
      <View style={styles.amountField}>
        <Text style={styles.amountLabel}>{label}</Text>
        <TextInput
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor="#7D8596"
          keyboardType="decimal-pad"
          style={[styles.input, error ? styles.inputError : null]}
        />
        {error ? (
          <Text accessibilityRole="alert" style={styles.fieldErrorText}>
            {error}
          </Text>
        ) : null}
      </View>
    );
  }

  function handleUploadProgress(progress: ImageUploadProgress) {
    setUploadStatus(progress.message);
    setUploadPercent(
      typeof progress.percent === "number" ? progress.percent : null
    );
  }

  async function pickImage() {
    if (busyImage || busySubmit) return;

    if (!businessId) {
      Alert.alert("Missing business", "Go back and try again.");
      return;
    }

    const itemId = editingItem?.id ?? String(Date.now());

    setBusyImage(true);
    setUploadError(null);
    setUploadPercent(null);
    setUploadStatus("Preparing image...");
    try {
      const uploaded = await pickAndUploadItemImage({
        businessId,
        itemId,
        onStatusChange: setUploadStatus,
        onProgress: handleUploadProgress,
      });

      setImageUri(uploaded.url);
      setImageWidth(uploaded.width);
      setImageHeight(uploaded.height);
      setUploadPercent(100);
      setUploadStatus("Image added successfully.");
      setImageError(null);
    } catch (error: unknown) {
      if (isImageUploadCancelled(error)) {
        setUploadStatus(null);
        setUploadPercent(null);
        return;
      }

      const detail = getFriendlyImageUploadError(error);

      if (detail === ITEM_IMAGE_TOO_LARGE_MESSAGE) {
        setUploadStatus(null);
        setUploadPercent(null);
        setUploadError(detail);
        Alert.alert("Image too large", detail);
        return;
      }

      setUploadStatus(null);
      setUploadPercent(null);
      setUploadError(detail);
      Alert.alert("Upload failed", detail);
    } finally {
      setBusyImage(false);
    }
  }

  function clearImage() {
    setImageUri(null);
    setImageWidth(null);
    setImageHeight(null);
    setUploadError(null);
    setUploadStatus(null);
    setUploadPercent(null);
    setImageError(null);
  }

  function cancelEdit() {
    resetForm();
    onCancelEdit?.();
  }

  async function submit() {
    if (busyImage || busySubmit) return;

    const trimmed = name.trim();
    const nextNameError = trimmed ? null : "Enter an item name.";
    const nextImageError = imageUri ? null : "Upload an image for this item.";
    const descriptionWordCount = countWords(description);
    const nextDescriptionError =
      descriptionWordCount > ITEM_DESCRIPTION_MAX_WORDS
        ? `Keep the description to ${ITEM_DESCRIPTION_MAX_WORDS} words or fewer.`
        : null;
    let nextPriceAmountError: string | null = null;
    let nextPriceMinError: string | null = null;
    let nextPriceMaxError: string | null = null;
    let nextCustomPriceUnitError: string | null = null;

    if (priceType === "fixed" || priceType === "starting_from") {
      if (readPositiveAmount(priceAmountText) === null) {
        nextPriceAmountError = "Enter a KSh amount greater than 0.";
      }
    }

    if (priceType === "range") {
      const min = readPositiveAmount(priceMinText);
      const max = readPositiveAmount(priceMaxText);

      if (min === null) {
        nextPriceMinError = "Enter a KSh minimum greater than 0.";
      }

      if (max === null) {
        nextPriceMaxError = "Enter a KSh maximum greater than 0.";
      } else if (min !== null && min > max) {
        nextPriceMaxError = "Maximum must be at least the minimum.";
      }
    }

    if (
      itemPriceTypeUsesAmount(priceType) &&
      priceUnit === CUSTOM_ITEM_PRICE_UNIT
    ) {
      const customUnitValidation = validateCustomPriceUnit(customPriceUnit);
      nextCustomPriceUnitError = customUnitValidation.error;
    }

    setNameError(nextNameError);
    setImageError(nextImageError);
    setDescriptionError(nextDescriptionError);
    setPriceAmountError(nextPriceAmountError);
    setPriceMinError(nextPriceMinError);
    setPriceMaxError(nextPriceMaxError);
    setCustomPriceUnitError(nextCustomPriceUnitError);

    if (
      nextNameError ||
      nextImageError ||
      nextDescriptionError ||
      nextPriceAmountError ||
      nextPriceMinError ||
      nextPriceMaxError ||
      nextCustomPriceUnitError
    ) {
      if (nextNameError) {
        setTimeout(() => nameInputRef.current?.focus(), 0);
      }
      return;
    }

    if (!trimmed || !imageUri) return;

    setBusySubmit(true);

    try {
      if (isEditing) {
        if (!editingItem || !onUpdate) return;

        const updated: BusinessItem = {
          ...withoutOptionalItemDetails(editingItem),
          name: trimmed,
          imageUri,
          imageUrl: imageUri,
          imageFit: "cover",
          imageWidth,
          imageHeight,
          ...buildOptionalItemDetails(),
        };

        onUpdate(updated);
        return;
      }

      const now = Date.now();

      onAdd({
        id: String(now),
        name: trimmed,
        imageUri,
        imageUrl: imageUri,
        imageFit: "cover",
        imageWidth,
        imageHeight,
        ...buildOptionalItemDetails(),
        order: now,
        createdAt: now,
      });

      resetForm();
    } finally {
      setBusySubmit(false);
    }
  }

  const imageStatusText = busyImage
    ? typeof uploadPercent === "number" &&
      uploadStatus?.startsWith("Uploading image")
      ? `Uploading image... ${uploadPercent}%`
      : uploadStatus ?? "Preparing image..."
    : uploadError
    ? uploadError
    : imageUri
    ? uploadStatus ?? "Image added successfully."
    : "JPG, PNG, HEIC, or WebP images work best.";
  const descriptionWordCount = countWords(description);
  const showUnitSelector = itemPriceTypeUsesAmount(priceType);
  const normalizedCustomUnit = normalizeCustomPriceUnit(customPriceUnit);
  const customPricePreviewText =
    priceUnit === CUSTOM_ITEM_PRICE_UNIT && normalizedCustomUnit
      ? formatItemPrice({
          priceType,
          priceAmount: priceAmountText,
          priceMin: priceMinText,
          priceMax: priceMaxText,
          priceUnit: CUSTOM_ITEM_PRICE_UNIT,
          customPriceUnit: normalizedCustomUnit,
        })
      : "";

  return (
    <View style={styles.card}>
      {isEditing ? (
        <View style={styles.editingRow}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>Editing item</Text>
          </View>

          <Pressable
            onPress={cancelEdit}
            disabled={busyImage || busySubmit}
            style={styles.cancelBtn}
          >
            <Text style={styles.cancelBtnText}>Cancel</Text>
          </Pressable>
        </View>
      ) : null}

      <Text style={styles.label}>Item name</Text>
      <TextInput
        ref={nameInputRef}
        value={name}
        onChangeText={(value) => {
          setName(value);
          if (nameError) setNameError(null);
        }}
        placeholder="e.g. Pancake"
        placeholderTextColor="#7D8596"
        style={[styles.input, nameError ? styles.inputError : null]}
        returnKeyType="done"
        onSubmitEditing={submit}
      />

      {nameError ? (
        <Text accessibilityRole="alert" style={styles.fieldErrorText}>
          {nameError}
        </Text>
      ) : null}

      <Text style={styles.imageLabel}>Image</Text>

      <View style={[styles.imageRow, imageError ? styles.imageRowError : null]}>
        <View style={styles.previewWrap}>
          {imageUri ? (
            <Image
              source={{ uri: imageUri }}
              style={styles.preview}
              resizeMode="cover"
            />
          ) : (
            <View style={styles.previewPlaceholder}>
              <Text style={styles.previewPlaceholderText}>Required</Text>
            </View>
          )}
        </View>

        <View style={styles.imageActions}>
          <Pressable
            onPress={pickImage}
            style={[
              styles.secondaryBtn,
              busyImage || busySubmit ? styles.btnDisabled : null,
            ]}
            disabled={busyImage || busySubmit}
          >
            <Text style={styles.secondaryBtnText}>
              {busyImage
                ? imageStatusText
                : imageUri
                ? "Choose another image"
                : uploadError
                ? "Try another image"
                : "Upload image"}
            </Text>
          </Pressable>

          <Pressable
            onPress={clearImage}
            style={[
              styles.secondaryBtn,
              !imageUri || busyImage || busySubmit ? styles.btnDisabled : null,
            ]}
            disabled={!imageUri || busyImage || busySubmit}
          >
            <Text style={styles.secondaryBtnText}>Remove image</Text>
          </Pressable>
        </View>
      </View>

      {imageError ? (
        <Text accessibilityRole="alert" style={styles.imageErrorText}>
          {imageError}
        </Text>
      ) : null}

      <Text
        style={[
          styles.imageStatus,
          uploadError ? styles.imageStatusError : null,
        ]}
      >
        {imageStatusText}
      </Text>

      <View style={styles.labelRow}>
        <Text style={styles.label}>Short description (optional)</Text>
        <Text
          style={[
            styles.wordCounter,
            descriptionError ? styles.wordCounterError : null,
          ]}
        >
          {descriptionWordCount}/{ITEM_DESCRIPTION_MAX_WORDS} words
        </Text>
      </View>

      <TextInput
        value={description}
        onChangeText={(value) => {
          setDescription(value);
          if (
            descriptionError &&
            countWords(value) <= ITEM_DESCRIPTION_MAX_WORDS
          ) {
            setDescriptionError(null);
          }
        }}
        placeholder="Add a quick detail customers should know"
        placeholderTextColor="#7D8596"
        style={[
          styles.input,
          styles.textArea,
          descriptionError ? styles.inputError : null,
        ]}
        multiline
        textAlignVertical="top"
      />

      {descriptionError ? (
        <Text accessibilityRole="alert" style={styles.fieldErrorText}>
          {descriptionError}
        </Text>
      ) : null}

      <View style={styles.pricingSection}>
        <View style={styles.labelRow}>
          <Text style={styles.label}>Price type (optional)</Text>

          {priceType ? (
            <Pressable onPress={clearPriceType} style={styles.clearTextBtn}>
              <Text style={styles.clearText}>Clear</Text>
            </Pressable>
          ) : null}
        </View>

        <View style={styles.optionGrid}>
          {ITEM_PRICE_TYPE_OPTIONS.map((option) => {
            const selected = priceType === option.value;

            return (
              <Pressable
                key={option.value}
                onPress={() => handlePriceTypeChange(option.value)}
                style={[
                  styles.optionBtn,
                  selected ? styles.optionBtnSelected : null,
                ]}
              >
                <Text
                  style={[
                    styles.optionText,
                    selected ? styles.optionTextSelected : null,
                  ]}
                >
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {priceType === "fixed" || priceType === "starting_from" ? (
          <View style={styles.amountWrap}>
            {renderAmountInput({
              label: "KSh amount",
              value: priceAmountText,
              onChangeText: (value) =>
                setAmountText(setPriceAmountText, value, () =>
                  setPriceAmountError(null)
                ),
              error: priceAmountError,
              placeholder: "8000",
            })}
          </View>
        ) : null}

        {priceType === "range" ? (
          <View style={styles.amountRow}>
            {renderAmountInput({
              label: "Minimum KSh",
              value: priceMinText,
              onChangeText: (value) =>
                setAmountText(setPriceMinText, value, () =>
                  setPriceMinError(null)
                ),
              error: priceMinError,
              placeholder: "8000",
            })}

            {renderAmountInput({
              label: "Maximum KSh",
              value: priceMaxText,
              onChangeText: (value) =>
                setAmountText(setPriceMaxText, value, () =>
                  setPriceMaxError(null)
                ),
              error: priceMaxError,
              placeholder: "12000",
            })}
          </View>
        ) : null}

        {showUnitSelector ? (
          <View style={styles.unitWrap}>
            <Text style={styles.amountLabel}>Pricing unit (optional)</Text>

            <View style={styles.optionGrid}>
              {ITEM_PRICE_UNIT_OPTIONS.map((option) => {
                const selected = priceUnit === option.value;

                return (
                  <Pressable
                    key={option.value}
                    onPress={() => togglePriceUnit(option.value)}
                    style={[
                      styles.optionBtn,
                      selected ? styles.optionBtnSelected : null,
                    ]}
                  >
                    <Text
                      style={[
                        styles.optionText,
                        selected ? styles.optionTextSelected : null,
                      ]}
                    >
                      {option.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Pressable
              onPress={selectCustomPriceUnit}
              style={[
                styles.addUnitBtn,
                priceUnit === CUSTOM_ITEM_PRICE_UNIT
                  ? styles.optionBtnSelected
                  : null,
              ]}
            >
              <Text
                style={[
                  styles.optionText,
                  priceUnit === CUSTOM_ITEM_PRICE_UNIT
                    ? styles.optionTextSelected
                    : null,
                ]}
              >
                + Add unit
              </Text>
            </Pressable>

            {priceUnit === CUSTOM_ITEM_PRICE_UNIT ? (
              <View style={styles.customUnitWrap}>
                <View style={styles.customUnitHeader}>
                  <Text style={styles.amountLabel}>Custom unit</Text>

                  <Pressable
                    onPress={clearCustomPriceUnit}
                    style={styles.clearTextBtn}
                  >
                    <Text style={styles.clearText}>Clear</Text>
                  </Pressable>
                </View>

                <TextInput
                  value={customPriceUnit}
                  onChangeText={handleCustomPriceUnitChange}
                  onBlur={() =>
                    setCustomPriceUnit(
                      normalizeCustomPriceUnit(customPriceUnit)
                    )
                  }
                  placeholder="e.g. kg, acre, trip or night"
                  placeholderTextColor="#7D8596"
                  style={[
                    styles.input,
                    customPriceUnitError ? styles.inputError : null,
                  ]}
                />

                {customPriceUnitError ? (
                  <Text
                    accessibilityRole="alert"
                    style={styles.fieldErrorText}
                  >
                    {customPriceUnitError}
                  </Text>
                ) : (
                  <Text style={styles.customUnitHelper}>
                    Enter the unit only. Soko Grid adds {"'per'"} automatically.
                  </Text>
                )}

                {customPricePreviewText ? (
                  <Text style={styles.pricePreviewText}>
                    Price preview: {customPricePreviewText}
                  </Text>
                ) : null}
              </View>
            ) : null}
          </View>
        ) : null}
      </View>

      <Pressable
        onPress={submit}
        style={[
          styles.primaryBtn,
          busyImage || busySubmit ? styles.btnDisabled : null,
        ]}
        disabled={busyImage || busySubmit}
      >
        <Text style={styles.primaryBtnText}>
          {busySubmit
            ? "Saving..."
            : isEditing
            ? "Save changes"
            : "+ Add item"}
        </Text>
      </Pressable>

      <Text style={styles.tip}>
        Every item must include an image.
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 12,
    backgroundColor: "#0C1020",
  },

  editingRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    gap: 10,
  },

  badge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: "rgba(47,111,237,0.18)",
    borderWidth: 1,
    borderColor: "rgba(47,111,237,0.35)",
  },

  badgeText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "700",
    opacity: 0.95,
  },

  cancelBtn: {
    paddingHorizontal: 10,
    paddingVertical: 6,
  },

  cancelBtnText: {
    color: "#C7D2FE",
    fontSize: 12,
    fontWeight: "700",
  },

  label: {
    color: "#FFFFFF",
    fontSize: 13,
    opacity: 0.85,
    marginBottom: 6,
  },

  labelRow: {
    marginTop: 12,
    marginBottom: 6,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  wordCounter: {
    color: "#FFFFFF",
    opacity: 0.62,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "800",
  },

  wordCounterError: {
    color: "#FCA5A5",
    opacity: 1,
  },

  imageLabel: {
    color: "#FFFFFF",
    fontSize: 13,
    opacity: 0.85,
    marginTop: 12,
    marginBottom: 8,
  },

  input: {
    backgroundColor: "#0B0F1A",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 15,
  },

  textArea: {
    minHeight: 84,
    lineHeight: 20,
    paddingTop: 12,
  },

  inputError: {
    borderColor: "#F87171",
    backgroundColor: "#1F1117",
  },

  fieldErrorText: {
    marginTop: 6,
    color: "#FCA5A5",
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "800",
  },

  pricingSection: {
    marginTop: 2,
    marginBottom: 12,
  },

  clearTextBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },

  clearText: {
    color: "#C7D2FE",
    fontSize: 12,
    fontWeight: "800",
  },

  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },

  optionBtn: {
    minHeight: 40,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    backgroundColor: "rgba(255,255,255,0.06)",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },

  optionBtnSelected: {
    borderColor: "#93C5FD",
    backgroundColor: "rgba(47,111,237,0.28)",
  },

  optionText: {
    color: "#FFFFFF",
    opacity: 0.78,
    fontSize: 13,
    lineHeight: 17,
    fontWeight: "800",
  },

  optionTextSelected: {
    opacity: 1,
  },

  amountWrap: {
    marginTop: 10,
  },

  amountRow: {
    marginTop: 10,
    flexDirection: "row",
    gap: 10,
  },

  amountField: {
    flex: 1,
    minWidth: 0,
  },

  amountLabel: {
    marginBottom: 6,
    color: "#FFFFFF",
    opacity: 0.72,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "800",
  },

  unitWrap: {
    marginTop: 12,
  },

  addUnitBtn: {
    alignSelf: "flex-start",
    minHeight: 40,
    marginTop: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    backgroundColor: "rgba(255,255,255,0.06)",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },

  customUnitWrap: {
    marginTop: 10,
  },

  customUnitHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  customUnitHelper: {
    marginTop: 6,
    color: "#FFFFFF",
    opacity: 0.64,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "700",
  },

  pricePreviewText: {
    marginTop: 8,
    color: "#BFDBFE",
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "800",
  },

  imageRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 14,
  },

  imageRowError: {
    borderWidth: 1,
    borderColor: "#F87171",
    borderRadius: 14,
    padding: 8,
  },

  imageErrorText: {
    marginTop: -6,
    marginBottom: 12,
    color: "#FCA5A5",
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "800",
  },

  previewWrap: {
    width: 96,
  },

  preview: {
    width: 96,
    height: 96,
    borderRadius: 14,
    backgroundColor: "rgba(255,255,255,0.06)",
  },

  previewPlaceholder: {
    width: 96,
    height: 96,
    borderRadius: 14,
    backgroundColor: "rgba(255,255,255,0.06)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
    alignItems: "center",
    justifyContent: "center",
  },

  previewPlaceholderText: {
    color: "#FFFFFF",
    opacity: 0.55,
    fontSize: 12,
    fontWeight: "700",
  },

  imageActions: {
    flex: 1,
    gap: 10,
  },

  secondaryBtn: {
    minHeight: 44,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.06)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.10)",
    paddingHorizontal: 12,
  },

  secondaryBtnText: {
    color: "#FFFFFF",
    fontWeight: "700",
    fontSize: 14,
  },

  primaryBtn: {
    width: "100%",
    minHeight: 48,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#2F6FED",
  },

  primaryBtnText: {
    color: "#FFFFFF",
    fontWeight: "800",
    fontSize: 16,
  },

  imageStatus: {
    marginTop: -6,
    marginBottom: 12,
    color: "#FFFFFF",
    opacity: 0.72,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "700",
  },

  imageStatusError: {
    color: "#FCA5A5",
    opacity: 1,
  },

  btnDisabled: {
    opacity: 0.6,
  },

  tip: {
    marginTop: 10,
    color: "#FFFFFF",
    opacity: 0.65,
    fontSize: 12,
    lineHeight: 17,
  },
});
